import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import Services from './components/Services';
import Portfolio from './components/Portfolio';
import SEO from './components/SEO';
import Support from './components/Support';
import Pricing from './components/Pricing';
import Contact from './components/Contact';
import Footer from './components/Footer';
import SEOHead from '../../components/SEOHead';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://courtreporterswebsites.com';
  
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ProfessionalService",
        "@id": `${siteUrl}/#organization`,
        "name": "Court Reporters Websites",
        "url": siteUrl,
        "logo": `${siteUrl}/logo.png`,
        "description": "Professional court reporting website services for freelance court reporters. Custom 5-page websites with toll-free numbers, SEO optimization, and marketing support.",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "488 Madison Ave",
          "addressLocality": "New York",
          "addressRegion": "NY",
          "postalCode": "10022",
          "addressCountry": "US"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "40.7577",
          "longitude": "-73.9719"
        },
        "telephone": "+1-516-554-1040",
        "email": "nyccourtreporters@gmail.com",
        "priceRange": "$99",
        "areaServed": {
          "@type": "Country",
          "name": "United States"
        },
        "serviceType": "Court Reporting Website Design"
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "url": siteUrl,
        "name": "Court Reporters Websites",
        "description": "Professional website services for court reporters",
        "publisher": {
          "@id": `${siteUrl}/#organization`
        }
      },
      {
        "@type": "Service",
        "serviceType": "Website Design for Court Reporters",
        "provider": {
          "@id": `${siteUrl}/#organization`
        },
        "areaServed": {
          "@type": "Country",
          "name": "United States"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Court Reporter Website Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Professional Court Reporter Website Package",
                "description": "5-page custom website with toll-free number, SEO optimization, and marketing support"
              },
              "price": "99",
              "priceCurrency": "USD",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "99",
                "priceCurrency": "USD",
                "billingDuration": "P1M"
              }
            }
          ]
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How much does a court reporter website cost?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Our professional court reporter websites start at just $99 per month, which includes a toll-free number, SEO optimization, and ongoing marketing support. There are no long-term contracts, and you can cancel anytime."
            }
          },
          {
            "@type": "Question",
            "name": "What is included in the website package?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Each package includes a custom 5-page website, toll-free number, SEO optimization, contact form integration, SSL security certificate, mobile-responsive design, social media integration, ongoing support, analytics dashboard, and marketing assistance."
            }
          },
          {
            "@type": "Question",
            "name": "Are there any long-term contracts?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No, we believe in earning your business every month. You can cancel anytime with no penalties or hidden fees. We offer complete flexibility with month-to-month service."
            }
          },
          {
            "@type": "Question",
            "name": "Do you help with marketing the website?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes! We don't just build your website - we help you market it. Our package includes marketing support, strategies to promote your services, and ongoing assistance to help you land more work from agencies worldwide."
            }
          }
        ]
      }
    ]
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <SEOHead
        title="New York Court Reporting Websites - $99/Month with Toll-Free Number"
        description="Professional court reporting website services in New York starting at $99/month. Get a custom 5-page website with toll-free number, SEO optimization, and marketing support. No long-term contracts for freelance court reporters."
        keywords="court reporting websites, court reporter web design, freelance court reporters, legal transcription services, New York court reporters, toll-free number, affordable website design"
        canonical={siteUrl}
        ogImage={`${siteUrl}/og-image.jpg`}
        schema={schema}
      />
      <div className="min-h-screen bg-white font-['Inter']">
        {/* Navigation */}
        <nav
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            scrolled ? 'bg-white shadow-md' : 'bg-transparent'
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E8E] rounded-lg flex items-center justify-center">
                  <i className={`ri-quill-pen-line text-xl ${scrolled ? 'text-white' : 'text-white'}`}></i>
                </div>
                <div>
                  <div className={`font-['Playfair_Display'] text-xl font-bold leading-tight ${scrolled ? 'text-[#1A2332]' : 'text-white'}`}>
                    Court Reporters
                  </div>
                  <div className={`text-xs font-medium tracking-wide ${scrolled ? 'text-[#FF6B6B]' : 'text-[#FF6B6B]'}`}>
                    WEBSITES
                  </div>
                </div>
              </div>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('home')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-700 hover:text-[#FF6B6B]' : 'text-white hover:text-[#FF6B6B]'
                }`}
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-700 hover:text-[#FF6B6B]' : 'text-white hover:text-[#FF6B6B]'
                }`}
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('portfolio')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-700 hover:text-[#FF6B6B]' : 'text-white hover:text-[#FF6B6B]'
                }`}
              >
                Portfolio
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-700 hover:text-[#FF6B6B]' : 'text-white hover:text-[#FF6B6B]'
                }`}
              >
                Pricing
              </button>
              <button
                onClick={() => scrollToSection('seo')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-700 hover:text-[#FF6B6B]' : 'text-white hover:text-[#FF6B6B]'
                }`}
              >
                SEO
              </button>
              <button
                onClick={() => scrollToSection('support')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-700 hover:text-[#FF6B6B]' : 'text-white hover:text-[#FF6B6B]'
                }`}
              >
                Support
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-700 hover:text-[#FF6B6B]' : 'text-white hover:text-[#FF6B6B]'
                }`}
              >
                Contact
              </button>
            </div>

            <button
              onClick={() => scrollToSection('contact')}
              className="bg-[#FF6B6B] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#ff5252] transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Started
            </button>
          </div>
        </nav>

        {/* Sections */}
        <div id="home">
          <Hero onContactClick={() => scrollToSection('contact')} />
        </div>
        <div id="services">
          <Services />
        </div>
        <div id="portfolio">
          <Portfolio />
        </div>
        <div id="pricing">
          <Pricing onContactClick={() => scrollToSection('contact')} />
        </div>
        <div id="seo">
          <SEO />
        </div>
        <div id="support">
          <Support />
        </div>
        <div id="contact">
          <Contact />
        </div>
        <Footer onNavigate={scrollToSection} />
      </div>
    </>
  );
}
